import { useEffect } from 'react';
import type { Ride } from '../domain/ride';
import { lookupStartingPlace } from '../services/starting-place';
import { applyStartingPlaceTitle, unavailableStartingPlace, type PlaceTitleSettings } from './place-title-settings';
import type { StudioDraft } from './studio-draft';

/** Fills one new draft without replacing edits or applying a response to another ride. */
export function useStartingPlace(ride: Ride, settings: PlaceTitleSettings,
  update: (change: (current: StudioDraft) => StudioDraft) => unknown, suspended: boolean) {
  const point = (ride.sourcePoints ?? ride.points)[0];
  const latitude = point?.latitude;
  const longitude = point?.longitude;
  useEffect(() => {
    if (settings.lookup !== 'pending' || suspended) return;
    const controller = new AbortController();
    const lookup = async () => {
      // An immediate cleanup cancels before a request leaves the browser.
      await Promise.resolve();
      if (controller.signal.aborted) return;
      try {
        if (latitude === undefined || longitude === undefined) throw new Error('No starting point.');
        const title = await lookupStartingPlace({ latitude, longitude }, controller.signal);
        if (!controller.signal.aborted) update(current => ({ ...current,
          placeTitle: applyStartingPlaceTitle(current.placeTitle, title) }));
      } catch {
        if (!controller.signal.aborted) update(current => ({ ...current,
          placeTitle: unavailableStartingPlace(current.placeTitle) }));
      }
    };
    void lookup();
    return () => controller.abort();
  }, [ride.id, ride.sourceFingerprint, latitude, longitude, settings.lookup, suspended, update]);
}
